// verify-badge.js - "Copy verify badge" UX for STD (free-tier) plugin result cards.
//
// GTI Verify Badge = a free, share-token-backed, self-reproducing proof for any STD
// plugin result. On a std result card, one click "Copy verify badge":
//   (a) mints a READ-ONLY share token (Target=plugin:std:{id} + the frozen inputs) via
//       the EXISTING ShareController (open to any authenticated user for std plugins),
//   (b) derives the run's verdict token (EXACT/PRECISE/VALID/DEGRADED/UNRELIABLE/
//       CANCELLED/UNTRACKED) from geonum-trust.fromResult - the ONE honest verdict
//       source shared by every feature, and
//   (c) emits Markdown / HTML / iframe embed snippets whose IMAGE is a STATIC shields
//       SVG chosen by verdict (served from /badges/run-{v}.svg) and whose LINK is the
//       share/kiosk auto-run permalink (ABS + '/s/' + token).
//
// Opening that permalink boots the SPA kiosk, which auto-RE-RUNS the frozen config
// read-only (a genuine recomputation of the plugin's pure closed-form JS on the frozen
// inputs - never a cached echo), renders the verdict badge with NO user-visible word
// "trust" on the public surface, and offers a one-click Subscribe.
//
// The badge's honest claim is REPRODUCIBILITY + a run VERDICT + tamper-evidence, never
// "proven correct". The fingerprint (gvs:{hash}) content-addresses the run for citation;
// the token is authoritative for re-run. STD-only is enforced server-side at mint, so a
// verify link can never bypass the paywall to re-run a paid / GPU engine.
//
// This module is a STATIC import into the app.js bundle graph (via plugin-loader.js), so
// it MUST ship on every deploy or a 404 kills auto-login app-wide.

import { api } from './api.js';
import { fromResult } from './geonum-trust.js';

// Hardcoded production origin so the emitted snippets resolve when embedded OFF-site
// (a README / blog / X / Notion card must not depend on the visitor's dev host). Matches
// content_gti.plugin.es:634 (the sibling content-badge embed kit).
const ABS = 'https://gdbs.getvaultsync.com';

// ---------------------------------------------------------------------------
// cyrb53 - a deterministic, avalanching 53-bit string hash (bryc). Copy of the
// content_gti.plugin.es content-hash core (lines 331-347), generalized to hash an
// arbitrary string with an optional seed. NOT cryptographic (not collision-resistant
// against an adversary); it is a reproducible fingerprint for accidental-change /
// reproducibility checking - exactly the honest "tamper-evident" claim. Rendered as a
// fixed 13-hex string so the same run always cites the same fingerprint.
// ---------------------------------------------------------------------------
export function cyrb53(str, seed = 0) {
  const s = String(str == null ? '' : str);
  let h1 = 0xdeadbeef ^ seed, h2 = 0x41c6ce57 ^ seed;
  for (let i = 0; i < s.length; i++) {
    const ch = s.charCodeAt(i);
    h1 = Math.imul(h1 ^ ch, 2654435761);
    h2 = Math.imul(h2 ^ ch, 1597334677);
  }
  h1 = Math.imul(h1 ^ (h1 >>> 16), 2246822507);
  h1 ^= Math.imul(h2 ^ (h2 >>> 13), 3266489909);
  h2 = Math.imul(h2 ^ (h2 >>> 16), 2246822507);
  h2 ^= Math.imul(h1 ^ (h1 >>> 13), 3266489909);
  // 53-bit result -> a fixed 13 hex chars (high 21 bits from h2, low 32 bits from h1).
  const hi = (h2 >>> 0).toString(16).padStart(8, '0');
  const lo = (h1 >>> 0).toString(16).padStart(8, '0');
  return (hi + lo).slice(-13);
}

// Canonical, order-stable string of the run: sorted-key params + result status + a
// flattened summary. Two identical runs (same inputs, same closed-form output) hash to
// the same fingerprint; any change to an input or an output value changes it.
export function canonical(params, res) {
  const p = (params && typeof params === 'object') ? params : {};
  const sortedKeys = Object.keys(p).sort();
  const sortedParams = {};
  for (const k of sortedKeys) sortedParams[k] = p[k];
  const status = (res && res.status != null) ? String(res.status) : '';
  let summary = '';
  if (res && Array.isArray(res.summary)) {
    // Flatten each [label, value] row to a stable "label=value" pair, joined by '|'.
    summary = res.summary.map(row => Array.isArray(row)
      ? row.map(cell => String(cell == null ? '' : cell)).join('=')
      : String(row == null ? '' : row)).join('|');
  }
  return JSON.stringify({ params: sortedParams, status, summary });
}

// ---------------------------------------------------------------------------
// verdictOf - the ONE verdict derivation for the public badge. Mirrors the
// plugin-loader trustSrc precedence: prefer an explicit res.trust, else the raw result
// of the last engine() call (host._lastResult), else the structured result. Pure-JS std
// plugins route no drift -> fromResult returns null -> honest 'UNTRACKED'. NEVER the word
// "trust" on any public surface: this returns only the verdict TOKEN.
// ---------------------------------------------------------------------------
export function verdictOf(res, host) {
  const src = (res && res.trust != null)
    ? res.trust
    : ((host && host._lastResult != null) ? host._lastResult : res);
  const t = fromResult(src);
  return (t && t.trust) ? String(t.trust).toUpperCase() : 'UNTRACKED';
}

// ---------------------------------------------------------------------------
// buildRunEmbed - assemble the embed kit from the shared URL-scheme templates.
// Returns { v, V, md, html, iframe, permalink, fp, badgeUrl }.
//   v = lowercased verdict token (drives the static /badges/run-{v}.svg filename)
//   V = uppercase verdict token (human-readable, used in alt / title / label text)
// The reproducing config travels IN the token record (server-side), NOT in query params,
// so the permalink cannot be tampered client-side. `url` (the server's own ShareUrl) is
// accepted for parity, but the permalink is rebuilt from ABS so it is always an absolute
// production URL regardless of the minting host.
// ---------------------------------------------------------------------------
export function buildRunEmbed({ token, url, verdict, hash }) {
  const V = String(verdict || 'UNTRACKED').toUpperCase();
  const v = V.toLowerCase();
  const permalink = ABS + '/s/' + token;
  const badgeUrl = ABS + '/badges/run-' + v + '.svg';
  const fp = 'gvs:' + hash;
  const md = '[![GTI ' + V + '](' + badgeUrl + ')](' + permalink + ')';
  const html = '<a href="' + permalink + '"><img src="' + badgeUrl + '" alt="GTI verdict ' + V + ' - re-run in browser"></a>';
  const iframe = '<iframe src="' + ABS + '/embed-run.html?v=' + v + '&t=' + token + '&h=' + hash + '" width="390" height="78" style="border:0;" title="GTI verdict ' + V + '"></iframe>';
  return { v, V, md, html, iframe, permalink, fp, badgeUrl };
}

// ---- tiny DOM helper (self-contained; avoids importing the plugin-host h()) ----------
function el(tag, style, text) {
  const node = document.createElement(tag);
  if (style) node.style.cssText = style;
  if (text != null) node.textContent = text;
  return node;
}

// Clipboard copy with an execCommand fallback for browsers that block the async
// Clipboard API (e.g. no user-gesture context / insecure origin). Flashes "Copied" on
// the triggering button for 1.5s, restoring its original label afterward.
function copyToClipboard(text, btn) {
  const original = btn.textContent;
  const flash = () => {
    btn.textContent = 'Copied';
    setTimeout(() => { btn.textContent = original; }, 1500);
  };
  const fallback = () => {
    try {
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.style.cssText = 'position:fixed;top:0;left:0;opacity:0;pointer-events:none;';
      document.body.appendChild(ta);
      ta.focus(); ta.select();
      try { document.execCommand && document.execCommand('copy'); } catch (e) { /* best effort */ }
      ta.remove();
      flash();
    } catch (e) { /* clipboard unavailable - leave the value visible to copy manually */ }
  };
  try {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(text).then(flash, fallback);
    } else {
      fallback();
    }
  } catch (e) { fallback(); }
}

// One "copy row": a label, a readonly value box (mono, wrapped), and a Copy button.
function copyRow(label, value) {
  const row = el('div', 'display:flex;flex-direction:column;gap:3px;margin-top:8px;');
  row.appendChild(el('div', 'font-size:10px;font-weight:700;color:#475569;text-transform:uppercase;letter-spacing:.03em;', label));
  const line = el('div', 'display:flex;gap:6px;align-items:stretch;');
  const box = el('textarea', 'flex:1;min-width:0;font-family:ui-monospace,SFMono-Regular,Menlo,monospace;font-size:11px;line-height:1.4;color:#1e293b;background:#f8fafc;border:1px solid #e2e8f0;border-radius:6px;padding:5px 7px;resize:none;overflow:auto;white-space:pre-wrap;word-break:break-all;');
  box.value = value;
  box.readOnly = true;
  box.rows = value.length > 60 ? 2 : 1;
  box.addEventListener('focus', () => { try { box.select(); } catch (e) {} });
  const copy = el('button', 'flex:0 0 auto;align-self:flex-start;font-size:11px;font-weight:600;padding:5px 10px;border:1px solid #cbd5e1;border-radius:6px;background:#fff;color:#334155;cursor:pointer;white-space:nowrap;', 'Copy');
  copy.type = 'button';
  copy.addEventListener('click', () => copyToClipboard(value, copy));
  line.appendChild(box);
  line.appendChild(copy);
  row.appendChild(line);
  return row;
}

// ---------------------------------------------------------------------------
// mountVerifyBadge - append (and, on click, open) the "Copy verify badge" affordance
// under a std plugin's result output. Only for std plugins; a no-op otherwise.
//
// Stale-guard: any prior badge button/popover is removed before appending a fresh one
// (this function is re-invoked after every successful run), AND a one-time input/change
// listener on the form removes the affordance the moment inputs change - so a copied
// badge always matches the on-screen result.
// ---------------------------------------------------------------------------
export async function mountVerifyBadge(anchorEl, { m, form, params, res, host }) {
  if (!anchorEl || !m || m.tier !== 'std') return;

  // Stale-guard: drop any badge affordance left from a previous run under this anchor.
  anchorEl.querySelectorAll('.verify-badge-btn, .verify-badge-pop').forEach(n => n.remove());

  // Light report-card button (plugin UI convention: light card, verdict-after-run).
  const btn = el('button',
    'margin-top:12px;display:inline-flex;align-items:center;gap:7px;font-size:12px;font-weight:600;padding:7px 13px;border:1px solid #cbd5e1;border-radius:8px;background:#fff;color:#0e7490;cursor:pointer;box-shadow:0 1px 2px rgba(15,23,42,0.06);');
  btn.type = 'button';
  btn.className = 'verify-badge-btn';
  btn.innerHTML = '<i class="fas fa-shield-halved"></i> Copy verify badge';
  anchorEl.appendChild(btn);

  // The affordance must not outlive the result it certifies. If the user edits any input
  // without re-running, remove the button + popover so a stale badge can't be copied.
  const dropOnChange = () => {
    btn.remove();
    anchorEl.querySelectorAll('.verify-badge-pop').forEach(n => n.remove());
    if (form) { form.removeEventListener('input', dropOnChange); form.removeEventListener('change', dropOnChange); }
  };
  if (form) { form.addEventListener('input', dropOnChange); form.addEventListener('change', dropOnChange); }

  let building = false;
  btn.addEventListener('click', async () => {
    if (building) return;
    // Toggle: a second click closes an already-open popover.
    const existing = anchorEl.querySelector('.verify-badge-pop');
    if (existing) { existing.remove(); return; }

    building = true;
    const origHtml = btn.innerHTML;
    btn.disabled = true;
    btn.innerHTML = '<i class="fas fa-circle-notch fa-spin"></i> Minting verify link...';

    // (1) Mint a READ-ONLY share token bound to this std plugin + the SAME frozen inputs
    // that produced the on-screen result. Mint requires [Authorize]; an anonymous visitor
    // gets no token -> a friendly sign-in prompt (free account, no card).
    let r = null;
    try { r = await api.createShare('plugin:std:' + m.id, m.name || m.id, params, undefined); }
    catch (e) { r = null; }

    btn.disabled = false;
    btn.innerHTML = origHtml;
    building = false;

    const pop = el('div', 'margin-top:10px;max-width:560px;border:1px solid #e2e8f0;border-radius:10px;background:#fff;color:#1e293b;box-shadow:0 8px 30px rgba(15,23,42,0.14);padding:14px 16px;');
    pop.className = 'verify-badge-pop';

    if (!r || !r.token) {
      // Not signed in (401/403) or the mint failed. Honest, actionable copy - never a
      // fabricated link. createShare returns { error } on a JSON failure body.
      const signInWall = (r && (r.status === 403)) ;
      const head = el('div', 'display:flex;align-items:center;gap:8px;margin-bottom:6px;font-weight:700;font-size:13px;color:#334155;');
      head.innerHTML = '<i class="fas fa-user-lock" style="color:#0e7490;"></i> Sign in (free) to mint a verify link';
      pop.appendChild(head);
      pop.appendChild(el('div', 'font-size:12px;color:#475569;line-height:1.5;',
        signInWall
          ? 'Only free Standard-tier results can be shared. This plugin is not eligible for a public verify link.'
          : 'A verify link is minted to your free account so it stays revocable and tamper-evident. Sign in or create a free account (no card), then click "Copy verify badge" again.'));
      if (r && r.error && !signInWall) {
        pop.appendChild(el('div', 'font-size:11px;color:#94a3b8;margin-top:6px;', String(r.error)));
      }
      const close = el('button', 'margin-top:10px;font-size:11px;font-weight:600;padding:5px 12px;border:1px solid #cbd5e1;border-radius:6px;background:#fff;color:#334155;cursor:pointer;', 'Close');
      close.type = 'button';
      close.addEventListener('click', () => pop.remove());
      pop.appendChild(close);
      anchorEl.appendChild(pop);
      return;
    }

    // (2) Derive the verdict token + the reproducible content fingerprint of this run.
    const verdict = verdictOf(res, host);
    const hash = cyrb53(canonical(params, res));
    const kit = buildRunEmbed({ token: r.token, url: r.url, verdict, hash });

    // (3) Render the popover: a live static-badge preview (exactly the image the snippet
    // embeds), a prominent primary "Copy badge (Markdown)", then per-format copy rows.
    const head = el('div', 'display:flex;align-items:center;gap:10px;flex-wrap:wrap;margin-bottom:4px;');
    const previewImg = el('img', 'height:20px;display:block;');
    previewImg.src = kit.badgeUrl;
    previewImg.alt = 'GTI verdict ' + kit.V + ' - re-run in browser';
    head.appendChild(previewImg);
    head.appendChild(el('div', 'font-weight:700;font-size:13px;color:#334155;', 'Verify badge - verdict ' + kit.V));
    const closeX = el('button', 'margin-left:auto;background:none;border:none;font-size:20px;line-height:1;color:#94a3b8;cursor:pointer;', '×');
    closeX.type = 'button';
    closeX.title = 'Close';
    closeX.addEventListener('click', () => pop.remove());
    head.appendChild(closeX);
    pop.appendChild(head);

    pop.appendChild(el('div', 'font-size:11px;color:#64748b;line-height:1.5;margin-bottom:4px;',
      'The badge links to a read-only page that RE-RUNS this exact result in the browser. The claim is reproducibility and tamper-evidence, not "proven correct". What you copy is what renders off-site.'));

    // Primary action: drop the Markdown badge straight into a README / blog / X / Notion.
    const primary = el('button', 'margin-top:10px;width:100%;display:inline-flex;align-items:center;justify-content:center;gap:8px;font-size:13px;font-weight:700;padding:9px 14px;border:1px solid #0e7490;border-radius:8px;background:#0e7490;color:#fff;cursor:pointer;', 'Copy badge (Markdown)');
    primary.type = 'button';
    primary.addEventListener('click', () => copyToClipboard(kit.md, primary));
    pop.appendChild(primary);

    // Per-format copy rows.
    pop.appendChild(copyRow('Badge - Markdown', kit.md));
    pop.appendChild(copyRow('Badge - HTML', kit.html));
    pop.appendChild(copyRow('Rich card - iframe', kit.iframe));
    pop.appendChild(copyRow('Verify link', kit.permalink));
    pop.appendChild(copyRow('Content fingerprint (cite as)', kit.fp));

    pop.appendChild(el('div', 'font-size:10px;color:#94a3b8;line-height:1.5;margin-top:10px;border-top:1px solid #eef2f7;padding-top:8px;',
      'Opening the link boots a read-only kiosk that re-runs the frozen inputs (a genuine recomputation, not a stored image) and shows the verdict badge. The fingerprint content-addresses the run for citation; the link is authoritative for re-run.'));

    anchorEl.appendChild(pop);
  });
}
